import * as React from 'react';
import { DataLayer, ProductsSd } from '@grandvisionhq/analytics';
import { ProductListing } from '@grandvisionhq/products';
import { useIntl } from '@grandvisionhq/state';
import '../Algolia/components/FacetsMobile/FacetsMobile.js';
import '../types.js';
import '../Algolia/components/CurrentRefinements/CurrentRefinements.js';
import '../Algolia/components/FacetInputList/ConnectedFacetInputList.js';
import '../Algolia/components/FacetInputPriceRange/ConnectedFacetInputPriceRange.js';
import '../Algolia/components/VirtualFacets/VirtualFacets.js';
import '../Algolia/components/SortBy/SortBy.js';
import '@swc/helpers';
import 'algoliasearch/lite';
import 'react-instantsearch-core';
import 'react-instantsearch-dom';
import 'react-instantsearch-dom/server';
import { useAlgoliaConfig } from '../Algolia/AlgoliaConfig.js';
import 'lodash/merge';
import '@grandvisionhq/utils';
import '../Algolia/utils/query-string.js';
import { useAlgoliaData } from '../Algolia/AlgoliaDataProvider.js';
import '../Algolia/AlgoliaAnalytics.js';
import '../CrossSellProducts/utils/create-algolia-filters.js';
import { getForwardedQueryParamsFromFacets } from '../Algolia/utils/get-forwarded-query-params-from-facets.js';
import InfinitePagination from '../InfinitePagination/InfinitePagination.js';
import { NoResults } from '../NoResults/NoResults.js';
import { createPagedHits } from './create-paged-hits.js';
import { useFormatProductLinks } from './hooks/use-format-product-links.js';

function ConnectedProductListing({ promotionBig , promotionSmall , reimbursableLogos  }) {
    const { getLabel  } = useIntl();
    const { pageSize , facets  } = useAlgoliaConfig();
    const { mappedResults: hits , total , searchState  } = useAlgoliaData();
    const { query , configure , refinementList  } = searchState;
    const page = (configure === null || configure === void 0 ? void 0 : configure.page) || 1;
    const length = (configure === null || configure === void 0 ? void 0 : configure.length) ? Number(configure.length) : page * pageSize;
    // Get the search parameters from the current URL and converts it to an object
    let searchParams;
    if (typeof window !== 'undefined') {
        searchParams = new URLSearchParams(window.location.search);
    }
    /**
   * If there are any search parameters (pre-applied filters) in the URL,
   * page_data_loaded event will be dispatched to the DataLayer with those filters.
   * We handle in this component because it provides access to the Facets array,
   * allowing us to obtain the labelKey of the facet based on the filter parameter (facet.id) specified in the URL.
   */ React.useEffect(()=>{
        if (searchParams && searchParams.toString() !== '') {
            const appliedFilters = {};
            searchParams.forEach((value, key)=>{
                let newKey = key // default labelKey is the key itself
                ;
                // loop through the facets array and assign facet.labelKey to newKey
                facets.forEach((facet)=>{
                    if (key === facet.id) {
                        newKey = getLabel(facet.labelKey || `facets.${key}`);
                    }
                });
                if (appliedFilters.hasOwnProperty(newKey)) {
                    if (!Array.isArray(appliedFilters[newKey])) {
                        appliedFilters[newKey] = [
                            appliedFilters[newKey]
                        ];
                    }
                    appliedFilters[newKey].push(value);
                } else {
                    appliedFilters[newKey] = value;
                }
            });
            const filterKeys = Object.keys(appliedFilters);
            const filterValues = filterKeys.map((key)=>{
                const values = appliedFilters[key];
                return Array.isArray(values) ? values.join('-') : values;
            });
            DataLayer.pushEvent({
                event: 'page_data_loaded',
                page: {
                    unixTimestamp: Math.floor(Date.now() / 1000),
                    type: 'product listing',
                    filter_type: filterKeys.join(', '),
                    filter_value: filterValues.join(', ')
                }
            });
        }
    }, []);
    /**
   * Facets can be configured to be forwarded on PDP urls when they are active
   * for prefilling information on the PDP
   */ const forwardedQueryParams = getForwardedQueryParamsFromFacets(facets, refinementList);
    /**
   * Creates a paged object of the hits we got from algolia.
   * If the amount of hits is lower than the amount of items we should render according to
   * the page number provided trough the URL, it means we should fill the page with the amount
   * of pages that can't be rendered yet, because the server-sided, only the last page is fetched
   * The maximum number of previous pages is capped to the number of products divided by page size
   */ const pages = React.useMemo(()=>{
        const totalNumberOfPages = Math.ceil(total / pageSize);
        const fill = length > hits.length && hits.length !== total ? Math.min(totalNumberOfPages, page) - 1 : 0;
        return createPagedHits(hits, total, pageSize, fill);
    }, [
        hits,
        total,
        pageSize,
        page,
        length
    ]);
    const links = useFormatProductLinks({
        products: hits
    });
    if (hits.length === 0) {
        return /*#__PURE__*/ React.createElement(NoResults, {
            title: getLabel('search.noResults.title'),
            description: getLabel('search.noResults.text'),
            query: query
        });
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ProductListing, {
        products: pages,
        showMoreButton: /*#__PURE__*/ React.createElement(InfinitePagination, null),
        promotionBig: promotionBig,
        promotionSmall: promotionSmall,
        reimbursableLogos: reimbursableLogos,
        forwardedQueryParams: forwardedQueryParams
    }), /*#__PURE__*/ React.createElement(ProductsSd, {
        items: hits,
        links: links
    }));
}

export { ConnectedProductListing, ConnectedProductListing as default };
//# sourceMappingURL=ConnectedProductListing.js.map
